import { useTranslation } from "react-i18next";
import type { ICustomField } from "store/customFieldsStore";

import DateFormComponent from "../FormComponent/DateFormComponent";
import NumberFormComponent from "../FormComponent/NumberFormComponent";
import RadioFormComponent from "../FormComponent/RadioFormComponent";
import TextAreaFormComponent from "../FormComponent/TextAreaFormComponent";
import TextFormComponent from "../FormComponent/TextFormComponent";
import UploadFileBtn from "../UploadFileBtn";

interface IFormCustomField {
    /**
     * Data of field being displayed.
     */
    field: ICustomField | undefined;
    /**
     * Index of the field withing the customFields array (useForm)
     */
    index: number;
    /**
     * Methods from useForm needed.
     */
    formData: { control: any; errors: any; watch: any; setValue: any };
    disabled?: boolean;
}

// Returns the needed field depending on the type:
const FormCustomField = ({ field, index, formData, disabled = false }: IFormCustomField) => {
    const [t] = useTranslation();
    const { control, errors, watch, setValue } = formData;

    if (field) {
        switch (field.valueType) {
            case "string":
                return (
                    <TextFormComponent
                        label={field.name}
                        control={control}
                        controlName={`customFields.${index}.value`}
                        error={
                            errors.customFields && errors.customFields[index] ? errors.customFields[index].value : null
                        }
                        required={!!field.isRequired}
                        disabled={disabled}
                    />
                );
            case "date":
                return (
                    <DateFormComponent
                        control={control}
                        controlName={`customFields.${index}.value`}
                        label={field.name}
                        error={
                            errors.customFields && errors.customFields[index] ? errors.customFields[index].value : null
                        }
                        required={!!field.isRequired}
                        disabled={disabled}
                    />
                );
            case "number":
                return (
                    <NumberFormComponent
                        control={control}
                        controlName={`customFields.${index}.value`}
                        label={field.name}
                        error={
                            errors.customFields && errors.customFields[index] ? errors.customFields[index].value : null
                        }
                        placeholder="0"
                        required={!!field.isRequired}
                        disabled={disabled}
                        acceptsNegative
                    />
                );
            case "longstring":
                return (
                    <TextAreaFormComponent
                        control={control}
                        controlName={`customFields.${index}.value`}
                        label={field.name}
                        minRows={4}
                        maxRows={12}
                        required={!!field.isRequired}
                        error={
                            errors.customFields && errors.customFields[index] ? errors.customFields[index].value : null
                        }
                        disabled={disabled}
                    />
                );
            case "fileimport":
                return (
                    <UploadFileBtn
                        label={field.isRequired ? `${field.name}*` : field.name}
                        control={control}
                        controlName={`customFields.${index}.value`}
                        watch={watch}
                        index={index}
                        setValue={setValue}
                        disabled={disabled}
                    />
                );
            case "boolean":
                return (
                    <RadioFormComponent
                        label={field.isRequired ? `${field.name}*` : field.name}
                        control={control}
                        controlName={`customFields.${index}.value`}
                        options={[
                            { label: t("CustomFields.CollapseInfo.Yes"), defaultChecked: true },
                            { label: t("CustomFields.CollapseInfo.No") },
                        ]}
                        error={
                            errors.customFields && errors.customFields[index] ? errors.customFields[index].value : null
                        }
                        required={!!field.isRequired}
                        disabled={disabled}
                    />
                );
        }
    } else {
        return;
    }
};

export default FormCustomField;
